import React, { useEffect, useState } from 'react';

import {
  Alert,
  Breakpoints,
  Card,
  CardSection,
  CTA,
  Download,
  TextAppearance,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
  useIsMobile,
} from '@vw-marketing/us-components';

import {
  AlertIconWrapper,
  CardDownloadLinkContainer,
  CardInformationContainer,
  CardInformationListContainer,
  CardTitleContainer,
  CardTitleWrapper,
  ErrorMessage,
  ErrorMessageContainer,
} from './styled';
import { AmbientLight } from '../../icons/AmbientLight';
import { fetchDocument } from '../../fetch-data/fetch-document';
import { getInfoListkeys, getTitleTranslationKey } from '../../utils/general';
import { onVWBasicLinkClickEvent } from '../../tagging/tagging-helpers';
import { OwnersLiteratureDocumentsData } from '../../typings/general';
import { useFeatureServices } from '../../hooks/use-feature-services';
import { useMaintenanceContext } from '../../hooks/use-maintenance-context';
import { useStore } from '../../hooks-store/store';
import { useTrackingManager } from '../../hooks/use-tracking-manager';

export const DownloadCard = ({
  doc,
  type,
  apiConfig
}: {
  doc: OwnersLiteratureDocumentsData;
  type?: string;
  apiConfig: string,
}) => {
  const [documentData, setDocumentData] = useState<Blob | null>(null);
  const [documentError, setDocumentError] = useState<boolean>(false);
  const [loadingDocument, setLoadingDocument] = useState<boolean>(false);

  const isDesktop: boolean = useIsMobile(Breakpoints.b1600);

  const [store] = useStore();
  const { 'service-config-provider': serviceProvider } = useFeatureServices();
  const { configs } = serviceProvider;
  const { vehicle } = store;
  const { translationHandler } = useMaintenanceContext();
  const faServicesUrl = configs['fa-services']?.urlOrigin ?? '';
  const infoKeys = getInfoListkeys(doc.document_type);
  const title = getTitleTranslationKey(doc.document_type);
  const trackingManager = useTrackingManager();

  const handleFetchDocument = (): void => {
    setLoadingDocument(true);

    const link = {
      name: doc.file_name
    }

    onVWBasicLinkClickEvent(
      trackingManager,
      undefined,
      'Download',
      doc,
      vehicle,
      'literature',
      link
    );

    fetchDocument({
      documentId: doc.id ?? '',
      faServicesUrl,
      apiKey: apiConfig,
    })
      .then((data: Blob) => {
        setDocumentData(data);
        setLoadingDocument(false);
      })
      .catch((error: any) => {
        setDocumentError(true);
        setLoadingDocument(false);
        throw new Error(`There was an error fetching the document: ${error}`);
      });
  };

  useEffect(() => {
    if (documentData) {
      const url = window.URL.createObjectURL(documentData);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }
  }, [documentData]);

  return (
    <Card>
      <CardTitleContainer type={type}>
        <TextCustom
          appearance={TextAppearance.headline200}
          tag={TextTag.p}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize16,
              fontWeight: TextWeight.bold,
              lineHeight: TextLineHeight.TextLineHeight20,
            },
            [Breakpoints.b1600]: {
              fontSize: TextSize.TextSize24,
              fontWeight: TextWeight.bold,
              lineHeight: TextLineHeight.TextLineHeight28,
            },
          }}
        >
          {translationHandler(title.titleKey || '')}
        </TextCustom>
      </CardTitleContainer>
      <CardSection>
        <CardInformationContainer type={type}>
          <CardTitleWrapper>
            <AmbientLight
              height={!isDesktop ? '24' : '20'}
              width={!isDesktop ? '24' : '20'}
            />
            <TextCustom
              appearance={TextAppearance.copy200}
              tag={TextTag.p}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize14,
                  fontWeight: TextWeight.bold,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize20,
                  fontWeight: TextWeight.bold,
                  lineHeight: TextLineHeight.TextLineHeight32,
                },
              }}
            >
              {translationHandler(
                'literature.downloadables.card.informationListTitle',
              )}
            </TextCustom>
          </CardTitleWrapper>
          <CardInformationListContainer>
            {Object.entries(infoKeys).map(([keyId, translationKey]) => (
              <TextCustom
                key={keyId}
                appearance={TextAppearance.copy200}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize14,
                    fontWeight: TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                  [Breakpoints.b1600]: {
                    fontSize: TextSize.TextSize20,
                    fontWeight: TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight32,
                  },
                }}
              >
                {translationHandler(translationKey)}
              </TextCustom>
            ))}
          </CardInformationListContainer>
        </CardInformationContainer>
      </CardSection>
      <CardSection>
        {documentError ? (
          <ErrorMessageContainer>
            <AlertIconWrapper>
              <Alert />
            </AlertIconWrapper>
            <ErrorMessage>
              <TextCustom
                appearance={TextAppearance.label100}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize14,
                    fontWeight: TextWeight.bold,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                {translationHandler(
                  'literature.downloadables.card.download.errorLabel',
                )}
              </TextCustom>
            </ErrorMessage>
          </ErrorMessageContainer>
        ) : (
          <CardDownloadLinkContainer>
            <CTA
              aria-label="Download document CTA"
              disabled={loadingDocument}
              emphasis="tertiary"
              onClick={handleFetchDocument}
              stretchContent
              tag="button"
            >
              <TextCustom
                appearance={TextAppearance.label100}
                tag={TextTag.p}
                style={{
                  [Breakpoints.default]: {
                    fontSize: TextSize.TextSize16,
                    fontWeight: TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight20,
                  },
                  [Breakpoints.b1600]: {
                    fontSize: TextSize.TextSize20,
                    fontWeight: TextWeight.regular,
                    lineHeight: TextLineHeight.TextLineHeight24,
                  },
                }}
              >
                <Download />{' '}
                {translationHandler(
                  'literature.downloadables.card.download.label',
                )}
              </TextCustom>
            </CTA>
          </CardDownloadLinkContainer>
        )}
      </CardSection>
    </Card>
  );
};
